import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api';
import type { ModuleViewProps } from '../../shell/types';
import {
  fetchCameras,
  fetchHealth,
  fetchQuality,
  saveCamera,
  saveSiteConfig,
  type CamerasResponse,
  type QualityReport,
  type StageLatency,
  type VendorHealthResponse,
} from './api';
import { lprStrings, type LprKey, type LprTranslate } from './strings';
import {
  Button,
  ModulePage,
  Reveal,
  Segmented,
  Select,
  Table,
  TableSkeleton,
  TextInput,
} from '../../shell/kit';
import { useToast } from '../../shell/toast';

const PERIODS = [
  { key: 'day', hours: 24, label: 'lpr.period.day' },
  { key: 'week', hours: 24 * 7, label: 'lpr.period.week' },
  { key: 'month', hours: 24 * 30, label: 'lpr.period.month' },
] as const;

type PeriodKey = (typeof PERIODS)[number]['key'];

const BUDGET_MS = 3000;

function percent(value: number): string {
  return `${Math.round(value * 1000) / 10}%`;
}

function Tile({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone?: 'warn' | 'bad';
  hint?: string;
}) {
  const color = tone === 'bad' ? 'text-danger' : tone === 'warn' ? 'text-warn' : 'text-ink';
  return (
    <div className="rounded-card border border-line bg-surface p-4">
      <div className="text-xs tracking-wide text-ink-muted uppercase">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular ${color}`}>{value}</div>
      {/* Подпись под числом: «прочитан номер» и «опознан клиент» — разные вещи,
          а на экране они стояли рядом под похожими словами. */}
      {hint ? <div className="mt-1 text-xs text-ink-muted">{hint}</div> : null}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface">
      <h3 className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">{title}</h3>
      <div className="p-4">{children}</div>
    </section>
  );
}

function LatencyTable({ latency, t }: { latency: readonly StageLatency[]; t: LprTranslate }) {
  const labels: Record<StageLatency['stage'], string> = {
    vendor: t('lpr.latency.vendor'),
    resolve: t('lpr.latency.resolve'),
    deliver: t('lpr.latency.deliver'),
    render: t('lpr.latency.render'),
    total: t('lpr.latency.total'),
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <thead>
          <tr className="text-left text-ink-muted">
            <th className="pb-2 font-medium">{t('lpr.latency.stage')}</th>
            <th className="pb-2 text-right font-medium">{t('lpr.latency.samples')}</th>
            <th className="pb-2 text-right font-medium">{t('lpr.latency.p50')}</th>
            <th className="pb-2 text-right font-medium">{t('lpr.latency.p95')}</th>
          </tr>
        </thead>
        <tbody>
          {latency.map((stage) => {
            const over = stage.stage === 'total' && (stage.p95Ms ?? 0) > BUDGET_MS;
            return (
              <tr key={stage.stage} className="border-t border-line">
                <td className="py-2 text-ink">{labels[stage.stage]}</td>
                <td className="py-2 text-right tabular-nums text-ink-muted">{stage.samples}</td>
                <td className="py-2 text-right tabular-nums text-ink-muted">
                  {stage.p50Ms ?? '—'}
                </td>
                <td
                  className={`py-2 text-right tabular-nums ${over ? 'font-semibold text-danger' : 'text-ink'}`}
                >
                  {stage.p95Ms ?? '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <p className="mt-3 text-xs text-ink-muted">{t('lpr.latency.budget')}</p>
      {/* Прочерк без объяснения читается как «метрика не работает». */}
      {latency.some((stage) => stage.stage === 'total' && stage.samples === 0) ? (
        <p className="mt-1 text-xs text-warn">{t('lpr.latency.noAcks')}</p>
      ) : null}
    </div>
  );
}

interface BreakdownRow {
  readonly key: string;
  readonly label: string;
  readonly total: number;
  readonly identified: number;
  readonly lowConfidence: number;
  readonly failed: number;
}

function BreakdownTable({
  rows,
  firstColumn,
  t,
  loading = false,
}: {
  rows: readonly BreakdownRow[];
  firstColumn: string;
  t: LprTranslate;
  loading?: boolean;
}) {
  // «Данных за период нет» — ответ, а не ожидание. Пока замер едет, показываем
  // скелет: иначе человек читает пустоту как результат и уходит.
  if (loading) return <TableSkeleton rows={3} columns={4} label={t('lpr.loading')} />;

  if (rows.length === 0) {
    return <p className="text-sm text-ink-muted">{t('lpr.breakdown.empty')}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <thead>
          <tr className="text-left text-ink-muted">
            <th className="pb-2 font-medium">{firstColumn}</th>
            <th className="pb-2 text-right font-medium">{t('lpr.breakdown.total')}</th>
            <th className="pb-2 text-right font-medium">{t('lpr.breakdown.identified')}</th>
            <th className="pb-2 text-right font-medium">{t('lpr.breakdown.low')}</th>
            <th className="pb-2 text-right font-medium">{t('lpr.breakdown.failed')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t border-line">
              <td className="py-2 text-ink">{row.label}</td>
              <td className="py-2 text-right tabular-nums text-ink-muted">{row.total}</td>
              <td className="py-2 text-right tabular-nums text-ink-muted">{row.identified}</td>
              <td className="py-2 text-right tabular-nums text-ink-muted">{row.lowConfidence}</td>
              <td className="py-2 text-right tabular-nums text-ink-muted">{row.failed}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export default function LprModule({ user, siteId, title }: ModuleViewProps) {
  const t = useMemo(() => lprStrings(user.locale), [user.locale]);

  const [period, setPeriod] = useState<PeriodKey>('day');
  const [cameras, setCameras] = useState<CamerasResponse | undefined>(undefined);
  const [quality, setQuality] = useState<QualityReport | undefined>(undefined);
  const [health, setHealth] = useState<VendorHealthResponse | undefined>(undefined);
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState<string | undefined>(undefined);

  const [cameraCode, setCameraCode] = useState('');
  const [cameraLabel, setCameraLabel] = useState('');
  const [cameraVendorId, setCameraVendorId] = useState('');
  const [cameraDirection, setCameraDirection] = useState<'entry' | 'exit'>('entry');

  const [threshold, setThreshold] = useState('');
  const [dedupWindow, setDedupWindow] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [minRate, setMinRate] = useState('');

  const load = useCallback(async (): Promise<void> => {
    if (!siteId) return;
    setBusy(true);
    const hours = PERIODS.find((p) => p.key === period)?.hours ?? 24;
    const from = new Date(Date.now() - hours * 3600_000);
    try {
      const [camerasData, qualityData] = await Promise.all([
        fetchCameras(siteId),
        fetchQuality(siteId, from),
      ]);
      setCameras(camerasData);
      setQuality(qualityData);
      if (camerasData.site) {
        setThreshold(String(camerasData.site.confidenceThreshold));
        setDedupWindow(String(camerasData.site.dedupWindowMs));
        setLatitude(camerasData.site.latitude == null ? '' : String(camerasData.site.latitude));
        setLongitude(camerasData.site.longitude == null ? '' : String(camerasData.site.longitude));
        setMinRate(String(camerasData.site.minRecognitionRate ?? 0.9));
      }
      try {
        setHealth(await fetchHealth(siteId));
      } catch {
        setHealth(undefined);
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
    } finally {
      setBusy(false);
    }
  }, [siteId, period]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!siteId) {
    return (
      <section className="flex h-full items-center justify-center p-10 text-ink-muted">
        {t('lpr.site.required')}
      </section>
    );
  }

  const onSaveCamera = async (): Promise<void> => {
    if (!cameraCode.trim()) return;
    setBusy(true);
    try {
      await saveCamera({
        siteId,
        code: cameraCode.trim(),
        label: cameraLabel.trim(),
        direction: cameraDirection,
        vendorCameraId: cameraVendorId.trim() || null,
      });
      setCameraCode('');
      setCameraLabel('');
      setCameraVendorId('');
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
      setBusy(false);
    }
  };

  const onSaveConfig = async (rotate: boolean): Promise<void> => {
    setBusy(true);
    try {
      const saved = await saveSiteConfig({
        siteId,
        confidenceThreshold: Number(threshold),
        dedupWindowMs: Number(dedupWindow),
        rotateSecret: rotate,
        latitude: latitude.trim() ? Number(latitude) : null,
        longitude: longitude.trim() ? Number(longitude) : null,
        ...(minRate.trim() ? { minRecognitionRate: Number(minRate) } : {}),
      });
      if (saved.secret) setSecret(saved.secret);
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
      setBusy(false);
    }
  };

  const totals = quality?.totals;
  const rates = quality?.rates;

  return (
    <ModulePage
      title={title || t('lpr.title')}
      subtitle={t('lpr.subtitle')}
      actions={
        <>
          <Segmented
            items={PERIODS.map((p) => ({ key: p.key, label: t(p.label) }))}
            active={period}
            onSelect={setPeriod}
          />
          <Button onClick={() => void load()} disabled={busy}>
            {busy ? t('lpr.loading') : t('lpr.refresh')}
          </Button>
        </>
      }
    >
      {health ? (
        <div
          className={`flex flex-wrap items-center gap-3 rounded border px-4 py-2 text-sm ${
            health.state.available
              ? 'border-ok-line bg-ok-bg text-ok'
              : 'border-danger-line bg-danger-bg text-danger'
          }`}
        >
          <span className="font-medium">
            {t('lpr.vendor.title')}: {health.vendor}
          </span>
          <span>
            {health.state.available ? t('lpr.vendor.available') : t('lpr.vendor.unavailable')}
          </span>
          <span className="text-xs opacity-80">
            {t('lpr.vendor.lastSeen')}:{' '}
            {health.state.lastSeen
              ? new Date(health.state.lastSeen).toLocaleString()
              : t('lpr.vendor.never')}
            {health.state.lastSeen ? null : (
              <span className="block text-xs text-ink-faint">{t('lpr.vendor.manualOnly')}</span>
            )}
          </span>
        </div>
      ) : null}

      {cameras && !cameras.site ? (
        <div className="rounded border border-warn-line bg-warn-bg px-4 py-2 text-sm text-warn">
          {t('lpr.notConfigured')}
        </div>
      ) : null}

      {totals && rates ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <Tile label={t('lpr.tiles.total')} value={String(totals.total)} />
          <Tile
            label={t('lpr.tiles.recognized')}
            value={percent(rates.recognized)}
            hint={t('lpr.tiles.recognizedHint')}
          />
          <Tile
            label={t('lpr.tiles.lowConfidence')}
            value={percent(rates.lowConfidence)}
            tone={rates.lowConfidence > 0.1 ? 'warn' : undefined}
          />
          <Tile label={t('lpr.tiles.manual')} value={percent(rates.manual)} />
          <Tile
            label={t('lpr.tiles.failed')}
            value={percent(rates.failed)}
            tone={rates.failed > 0.05 ? 'bad' : undefined}
          />
          <Tile
            label={t('lpr.tiles.unacked')}
            value={String(quality?.unacked ?? 0)}
            tone={(quality?.unacked ?? 0) > 0 ? 'warn' : undefined}
          />
        </div>
      ) : null}

      {quality ? (
        <Panel title={t('lpr.latency.title')}>
          <LatencyTable latency={quality.latency} t={t} />
        </Panel>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t('lpr.breakdown.camera')}>
          <BreakdownTable
            loading={!quality}
            firstColumn={t('lpr.cameras.code')}
            t={t}
            rows={(quality?.byCamera ?? []).map((row) => ({
              key: row.cameraId ?? 'none',
              label: row.cameraCode ?? '—',
              total: row.total,
              identified: row.identified,
              lowConfidence: row.lowConfidence,
              failed: row.failed,
            }))}
          />
        </Panel>

        <Panel title={t('lpr.breakdown.hour')}>
          <BreakdownTable
            loading={!quality}
            firstColumn={t('lpr.breakdown.hourColumn')}
            t={t}
            rows={(quality?.byHour ?? []).map((row) => ({
              key: String(row.hour),
              label: `${String(row.hour).padStart(2, '0')}:00`,
              total: row.total,
              identified: row.identified,
              lowConfidence: row.lowConfidence,
              failed: row.failed,
            }))}
          />
        </Panel>

        <Panel title={t('lpr.conditions.title')}>
          {!quality ? (
            <TableSkeleton rows={3} columns={5} />
          ) : (
            <Table>
              <thead>
                <tr className="text-left text-ink-muted">
                  <th className="pb-2 font-medium" />
                  <th className="pb-2 text-right font-medium">{t('lpr.conditions.attempts')}</th>
                  <th className="pb-2 text-right font-medium">{t('lpr.conditions.rate')}</th>
                  <th className="pb-2 text-right font-medium">{t('lpr.conditions.threshold')}</th>
                  <th className="pb-2 text-right font-medium" />
                </tr>
              </thead>
              <tbody>
                {(quality.conditions ?? []).map((row) => (
                  <tr key={row.condition}>
                    <td className="py-1">{t(`lpr.conditions.${row.condition}` as LprKey)}</td>
                    <td className="py-1 text-right tabular">{row.attempts}</td>
                    <td className="py-1 text-right tabular">{percent(row.rate)}</td>
                    <td className="py-1 text-right tabular">{percent(row.threshold)}</td>
                    <td className={`py-1 text-right ${row.pass === false ? 'text-danger' : row.pass ? 'text-ok' : 'text-ink-muted'}`}>
                      {row.pass === null
                        ? t('lpr.conditions.none')
                        : row.pass
                          ? t('lpr.conditions.pass')
                          : t('lpr.conditions.fail')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {quality?.cycle ? (
            <p className="mt-3 text-sm text-ink-muted">
              {t('lpr.cycle.title')}: {t('lpr.cycle.samples')} {quality.cycle.samples}
              {quality.cycle.avgSeconds !== null
                ? ` · ${t('lpr.cycle.avg')} ${Math.round(quality.cycle.avgSeconds / 60)} мин`
                : ''}
            </p>
          ) : null}
        </Panel>

        <Panel title={t('lpr.breakdown.weather')}>
          <BreakdownTable
            loading={!quality}
            firstColumn={t('lpr.breakdown.weather')}
            t={t}
            rows={(quality?.byWeather ?? []).map((row) => ({
              key: row.weather,
              label: row.weather,
              total: row.total,
              identified: row.identified,
              lowConfidence: row.lowConfidence,
              failed: row.failed,
            }))}
          />
        </Panel>

        <Panel title={t('lpr.licence.title')}>
          {!quality ? (
            <TableSkeleton rows={2} columns={4} />
          ) : (quality.licence ?? []).length === 0 ? (
            <p className="text-sm text-ink-muted">{t('lpr.breakdown.empty')}</p>
          ) : (
            <Table>
              <thead>
                <tr className="text-left text-ink-muted">
                  <th className="pb-2 font-medium">{t('lpr.config.vendor')}</th>
                  <th className="pb-2 text-right font-medium">{t('lpr.licence.calls')}</th>
                  <th className="pb-2 text-right font-medium">{t('lpr.licence.units')}</th>
                  <th className="pb-2 text-right font-medium">{t('lpr.licence.failures')}</th>
                </tr>
              </thead>
              <tbody>
                {(quality?.licence ?? []).map((row) => (
                  <tr key={row.vendor} className="border-t border-line">
                    <td className="py-2 text-ink">{row.vendor}</td>
                    <td className="py-2 text-right tabular-nums text-ink-muted">{row.calls}</td>
                    <td className="py-2 text-right tabular-nums text-ink-muted">{row.units}</td>
                    <td className="py-2 text-right tabular-nums text-ink-muted">{row.failures}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Panel>
      </div>

      <Panel title={t('lpr.cameras.title')}>
        {!cameras ? (
          <TableSkeleton rows={3} columns={5} />
        ) : (cameras.cameras ?? []).length === 0 ? (
          <p className="mb-4 text-sm text-ink-muted">{t('lpr.cameras.empty')}</p>
        ) : (
          <Table className="mb-4">
            <thead>
              <tr className="text-left text-ink-muted">
                <th className="pb-2 font-medium">{t('lpr.cameras.code')}</th>
                <th className="pb-2 font-medium">{t('lpr.cameras.label')}</th>
                <th className="pb-2 font-medium">{t('lpr.cameras.direction')}</th>
                <th className="pb-2 font-medium">{t('lpr.cameras.vendorId')}</th>
                <th className="pb-2 font-medium">{t('lpr.cameras.status')}</th>
              </tr>
            </thead>
            <tbody>
              {(cameras?.cameras ?? []).map((camera) => (
                <tr key={camera.id} className="border-t border-line">
                  <td className="py-2 font-medium text-ink">{camera.code}</td>
                  <td className="py-2 text-ink-muted">{camera.label || '—'}</td>
                  <td className="py-2 text-ink-muted">
                    {camera.direction === 'entry' ? t('lpr.cameras.entry') : t('lpr.cameras.exit')}
                  </td>
                  <td className="py-2 text-ink-muted">{camera.vendorCameraId ?? '—'}</td>
                  <td className="py-2 text-ink-muted">
                    {camera.status === 'active'
                      ? t('lpr.cameras.active')
                      : t('lpr.cameras.disabled')}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <TextInput
            value={cameraCode}
            onChange={(e) => setCameraCode(e.target.value)}
            placeholder={t('lpr.cameras.code')}
          />
          <TextInput
            value={cameraLabel}
            onChange={(e) => setCameraLabel(e.target.value)}
            placeholder={t('lpr.cameras.label')}
          />
          <TextInput
            value={cameraVendorId}
            onChange={(e) => setCameraVendorId(e.target.value)}
            placeholder={t('lpr.cameras.vendorId')}
          />
          <Select
            value={cameraDirection}
            onChange={(e) => setCameraDirection(e.target.value === 'exit' ? 'exit' : 'entry')}
          >
            <option value="entry">{t('lpr.cameras.entry')}</option>
            <option value="exit">{t('lpr.cameras.exit')}</option>
          </Select>
          <button
            type="button"
            onClick={() => void onSaveCamera()}
            disabled={busy || !cameraCode.trim()}
            className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
          >
            {t('lpr.cameras.add')}
          </button>
        </div>
      </Panel>

      <Panel title={t('lpr.config.title')}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm text-ink-muted">
            {t('lpr.config.threshold')}
            <TextInput
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              inputMode="decimal"
              className="mt-1"
            />
          </label>
          <label className="text-sm text-ink-muted">
            {t('lpr.config.window')}
            <TextInput
              value={dedupWindow}
              onChange={(e) => setDedupWindow(e.target.value)}
              inputMode="numeric"
              className="mt-1"
            />
          </label>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => void onSaveConfig(false)}
              disabled={busy}
              className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
            >
              {t('lpr.config.save')}
            </button>
            <button
              type="button"
              onClick={() => void onSaveConfig(true)}
              disabled={busy}
              className="inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint"
            >
              {t('lpr.config.rotate')}
            </button>
          </div>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <label className="text-sm text-ink-muted">
            {t('lpr.config.latitude')}
            <TextInput value={latitude} onChange={(e) => setLatitude(e.target.value)} inputMode="decimal" className="mt-1" />
          </label>
          <label className="text-sm text-ink-muted">
            {t('lpr.config.longitude')}
            <TextInput value={longitude} onChange={(e) => setLongitude(e.target.value)} inputMode="decimal" className="mt-1" />
          </label>
          <label className="text-sm text-ink-muted">
            {t('lpr.config.minRate')}
            <TextInput value={minRate} onChange={(e) => setMinRate(e.target.value)} inputMode="decimal" className="mt-1" />
          </label>
        </div>
        <p className="mt-3 text-xs text-ink-muted">{t('lpr.config.thresholdHint')}</p>
        <p className="mt-1 text-xs text-ink-muted">{t('lpr.config.coordsHint')}</p>
        {/* Секрет показывается один раз и стоит внизу настройки — без прокрутки
            к себе перевыпуск выглядел бы как «ничего не произошло». */}
        {secret ? (
          <Reveal when={secret}>
          <div className="mt-3 rounded border border-warn-line bg-warn-bg px-3 py-2 text-sm">
            <div className="text-warn">{t('lpr.config.secret')}</div>
            <code className="mt-1 block break-all font-mono text-xs text-ink">{secret}</code>
          </div>
          </Reveal>
        ) : null}
      </Panel>
    </ModulePage>
  );
}
